import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconAlertTriangle, IconShieldLock} from '@tabler/icons-react';
import {fetchMFAPreference, fetchUserAttributes} from 'aws-amplify/auth';
import {useEffect, useState} from 'react';
import {Button} from 'src/design/atoms/Button';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {Pill} from 'src/design/atoms/Pill';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {Spinner} from 'src/design/atoms/Spinner';
import {RefreshButton} from 'src/design/molecules/RefreshButton';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {ServerErrorAlert} from 'src/app/layout/ServerErrorAlert';
import {
  parseErrorCode,
  parseErrorMessage,
} from 'src/shared/utils/parseErrorMessage';
import {useDisableOwnMfaMutation} from 'src/redux/queries/user';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {MfaSetupPanel} from './components/MfaSetupPanel';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const PAGE_TITLE = 'Multi-Factor Authentication';

type Status = 'loading' | 'enabled' | 'disabled' | 'federated' | 'error';

const REAUTHENTICATION_REQUIRED = 'REAUTHENTICATION_REQUIRED';

export const SecurityPage = () => {
  useSetDocumentTitle(PAGE_TITLE);
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [status, setStatus] = useState<Status>('loading');
  const [reauthRequired, setReauthRequired] = useState(false);
  const [username, setUsername] = useState('');
  const [settingUp, setSettingUp] = useState(false);
  const [confirmingOff, setConfirmingOff] = useState(false);
  const [turnOffError, setTurnOffError] = useState<string>();
  const [error, setError] = useState<string>();
  const [disableOwnMfa, {isLoading: turningOff}] = useDisableOwnMfaMutation();

  const load = async () => {
    setStatus('loading');
    setError(undefined);
    try {
      const attributes = await fetchUserAttributes();
      if (attributes.identities) {
        setStatus('federated');
        return;
      }
      setUsername(attributes.email ?? 'Proact user');
      const preference = await fetchMFAPreference();
      setStatus(preference.enabled?.includes('TOTP') ? 'enabled' : 'disabled');
    } catch (err) {
      setError(parseErrorMessage(err));
      setStatus('error');
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const turnOff = async () => {
    setTurnOffError(undefined);
    try {
      await disableOwnMfa().unwrap();
      setConfirmingOff(false);
      setStatus('disabled');
      setAlert('Multi-factor authentication was turned off', 'success');
    } catch (err) {
      setConfirmingOff(false);
      if (parseErrorCode(err) === REAUTHENTICATION_REQUIRED) {
        setReauthRequired(true);
      } else {
        setTurnOffError(parseErrorMessage(err));
      }
    }
  };

  const signInAgain = () =>
    navigate(
      `${PathUtility.Logout()}?returnUrl=${encodeURIComponent(
        PathUtility.Security()
      )}`
    );

  const statusPill =
    status === 'enabled' ? (
      <Pill text="On" color="green" />
    ) : status === 'disabled' ? (
      <Pill text="Off" color="gray" />
    ) : status === 'federated' ? (
      <Pill text="Managed" color="gray" />
    ) : undefined;

  return (
    <ContentWrapper>
      <BackBar
        fallbackTo={PathUtility.Dashboard()}
        breadcrumbs={[{label: PAGE_TITLE}]}
      />
      <PageHeader>
        <PageHeader.Title
          icon={<IconShieldLock />}
          subtitle="Protect your account with a second sign-in step.">
          {PAGE_TITLE}
        </PageHeader.Title>
      </PageHeader>
      <Box mt="md" maw={720}>
        <SectionCard title="Authenticator app" rightSection={statusPill}>
          <Stack p="md">
            {status === 'loading' && (
              <Spinner loadingText="Checking your settings..." />
            )}
            {status === 'error' && (
              <>
                <ServerErrorAlert message={error} />
                <Box>
                  <RefreshButton
                    onClick={() => {
                      void load();
                    }}>
                    Retry
                  </RefreshButton>
                </Box>
              </>
            )}
            {status === 'federated' && (
              <Text size="sm">
                Your organization manages how you sign in. Contact your
                administrator to change multi-factor authentication.
              </Text>
            )}
            {status === 'enabled' && (
              <>
                <Text size="sm" role="status">
                  You will enter a code from your authenticator app each time
                  you sign in.
                </Text>
                <Text size="sm" c="dimmed">
                  Lost your device? Contact support.
                </Text>
                <ServerErrorAlert message={turnOffError} />
                {reauthRequired ? (
                  <Alert
                    variant="light"
                    color="yellow"
                    icon={<IconAlertTriangle />}
                    title="Sign in again to continue">
                    <Stack gap="xs" align="flex-start">
                      <Text size="sm">
                        For your security, sign in again and then turn off
                        multi-factor authentication.
                      </Text>
                      <Button onClick={signInAgain}>Sign in again</Button>
                    </Stack>
                  </Alert>
                ) : (
                  <Box>
                    <Button color="red" onClick={() => setConfirmingOff(true)}>
                      Turn off
                    </Button>
                  </Box>
                )}
                <ConfirmDialog
                  opened={confirmingOff}
                  onClose={() => setConfirmingOff(false)}
                  onConfirm={() => {
                    void turnOff();
                  }}
                  title="Turn off multi-factor authentication?"
                  body="You will sign in with your password or an email sign-in link only. You can turn it on again at any time."
                  confirmationButtonColor="red"
                  buttonTextYes="Turn off"
                  confirmationButtonDisabled={turningOff}
                />
              </>
            )}
            {status === 'disabled' && !settingUp && (
              <>
                <Text size="sm">
                  Multi-factor authentication is off. Turn it on to require a
                  code from an authenticator app when you sign in.
                </Text>
                <Box>
                  <Button onClick={() => setSettingUp(true)}>Turn on</Button>
                </Box>
              </>
            )}
            {status === 'disabled' && settingUp && (
              <MfaSetupPanel
                username={username}
                onCancel={() => setSettingUp(false)}
                onEnabled={() => {
                  setSettingUp(false);
                  setStatus('enabled');
                  setAlert('Multi-factor authentication was turned on', 'success');
                }}
              />
            )}
          </Stack>
        </SectionCard>
      </Box>
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.Security(),
  element: <SecurityPage />,
  isPrivate: true,
});
